import { useEffect, useMemo, useRef, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { campaignsI18n } from './i18n';

export default function CreateCampaignsModal({
  Modal,
  Input,
  Dropdown,
  ComboBox,
  Button,
  show,
  mode = 'create', // 'create' | 'edit'
  initialData = null,
  onClose,
  onSave,
  templateOptions = [],
}) {
  const { t, language: campLanguage } = useScopedTranslation(campaignsI18n);

  const channelOptions = useMemo(
    () => [
      { label: t('channel_whatsapp'), value: 'whatsapp' },
      { label: t('channel_sms'), value: 'sms' },
      { label: t('channel_email'), value: 'email' },
    ],
    [campLanguage]
  );

  const [form, setForm] = useState({
    name: '',
    channel: '',
    template: null,
  });

  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    if (mode === 'edit' && initialData) {
      setForm({
        name: initialData.name || '',
        channel: initialData.channel || initialData.deliveryType || '',
        template:
          templateOptions.find(
            (tmpl) =>
              tmpl.value === initialData.templateId ||
              tmpl.value === initialData.template ||
              tmpl.label === initialData.templateName
          ) || null,
      });
      setFile(null);
    } else {
      setForm({
        name: '',
        channel: channelOptions[0]?.value || '',
        template: null,
      });
      setFile(null);
    }
    setIsDragging(false);
  }, [show, mode, initialData, channelOptions, templateOptions]);

  const setField = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

  const effectiveTemplateOptions =
    templateOptions && templateOptions.length > 0 ? templateOptions : [];

  const isValid =
    form.name.trim().length > 2 && !!form.channel && !!form.template && !!file;

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (filesList) => {
    const selected = filesList?.[0];
    if (!selected) return;
    setFile(selected);
  };

  const handleFileChange = (e) => {
    handleFilesSelected(e.target.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) {
      handleFilesSelected(e.dataTransfer.files);
    }
  };

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await onSave?.({
        id: initialData?.id,
        name: form.name.trim(),
        channel: form.channel,
        templateId: form.template?.value ?? null,
        templateLabel: form.template?.label ?? '',
        file,
        mode,
      });
      setSaving(false);
      onClose?.();
    } catch (error) {
      console.error('Error saving campaign:', error);
      setSaving(false);
      alert(t('modal_error_save'));
    }
  };

  if (!Modal || !Input || !Dropdown || !Button) return null;
  if (!show) return null;

  const title =
    mode === 'edit' ? t('modal_title_edit') : t('modal_title_create');

  const primaryLabel = saving
    ? t('modal_btn_saving')
    : mode === 'edit'
      ? t('modal_btn_save')
      : t('modal_btn_create');

  return (
    <Modal
      title={title}
      show={show}
      onClose={onClose}
      primaryBtnTxt={primaryLabel}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || saving}
      secondaryBtnTxt={t('modal_btn_cancel')}
      secondaryBtnAction={onClose}
      width={960}
      height='80vh'
    >
      <div className='d-flex flex-column gap-3' style={{ maxHeight: '72vh' }}>
        <div className='row g-2'>
          <div className='col-12'>
            <Input
              label={t('field_name_label')}
              placeholder={t('field_name_placeholder')}
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </div>
        </div>

        {/* Canal y plantilla */}
        <div className='row g-2'>
          <div className='col-12 col-md-6'>
            <Dropdown
              label={t('field_channel_label')}
              placeholder={t('field_channel_placeholder')}
              options={channelOptions}
              value={channelOptions.find((o) => o.value === form.channel)}
              onChange={(selected) =>
                setField('channel', selected?.value ?? '')
              }
            />
          </div>
          <div className='col-12 col-md-6'>
            {ComboBox ? (
              <ComboBox
                label={t('field_template_label')}
                placeholder={t('field_template_placeholder')}
                options={effectiveTemplateOptions}
                value={form.template}
                onChange={(selected) => setField('template', selected)}
                width='100%'
              />
            ) : (
              <Dropdown
                label={t('field_template_label')}
                placeholder={t('field_template_placeholder')}
                options={effectiveTemplateOptions}
                value={form.template}
                onChange={(selected) => setField('template', selected)}
              />
            )}
          </div>
        </div>

        {/* Archivo de destinatarios */}
        <div className='row g-2'>
          <div className='col-12'>
            <div className='mb-1 mini fw-semibold'>{t('file_label')}</div>
            <div
              className='d-flex flex-column align-items-center justify-content-center text-center rounded-3'
              style={{
                border: '1px dashed var(--gray-300)',
                padding: '32px 16px',
                backgroundColor: isDragging
                  ? 'var(--primary-50, #f0f7ff)'
                  : 'var(--bg-body, #ffffff)',
                cursor: 'pointer',
              }}
              onClick={handleFileClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className='mb-3'>
                <div
                  className='d-inline-flex align-items-center justify-content-center rounded-circle'
                  style={{
                    width: 40,
                    height: 40,
                    border: '1px solid var(--gray-300)',
                  }}
                >
                  <span
                    className='material-icons-outlined'
                    style={{ fontSize: 20 }}
                  >
                    file_upload
                  </span>
                </div>
              </div>
              <div className='mb-1'>
                <div className='caption'>{t('file_drag_hint')}</div>
              </div>
              <div className='mini font-light mb-3'>{t('file_formats')}</div>
              <Button
                size='sm'
                variant='secondary'
                label={file ? t('file_btn_change') : t('file_btn_select')}
              />
              {file && (
                <div className='mini font-light mt-2'>
                  {t('file_selected')}{' '}
                  <span className='fw-semibold'>{file.name}</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type='file'
                accept='.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .xlsx'
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
